/**
 * Die Regel eines Speichers für die Wohnungen (HM-172, ADR 0050) — als
 * Formular: Prozent und Kilowatt statt Watt, mit Prüfung am Feld.
 *
 * Die Regeln selbst (erste Stufe bei 0 %, steigende Stände, Grenzen) stehen in
 * der Domäne; hier sind sie abgeschrieben, damit das Formular vor dem Absenden
 * sagt, was nicht stimmt. `storage-tenant-policy.test.ts` hält Vorgabe und
 * Grenzen gegen die Domäne.
 */
import type { ApiComponents } from "@hausmeter/sdk";

export type StorageTenantPolicy = ApiComponents["schemas"]["StorageTenantPolicy"];
type Band = StorageTenantPolicy["bands"][number];

export const MAX_BANDS = 10;
export const MAX_KW = 100;

/** Die Vorgabe der Domäne (`DEFAULT_STORAGE_TENANT_POLICY`). */
export const DEFAULT_POLICY: StorageTenantPolicy = {
  bands: [
    { fromSocPercent: 0, dischargeW: 0, chargeReserveW: null },
    { fromSocPercent: 30, dischargeW: 1000, chargeReserveW: 2000 },
    { fromSocPercent: 70, dischargeW: 3000, chargeReserveW: 500 },
    { fromSocPercent: 95, dischargeW: 5000, chargeReserveW: 0 },
  ],
};

export interface DraftBand {
  readonly from: string;
  readonly dischargeKw: string;
  /** Die ganze Ladeleistung bleibt beim Speicher. */
  readonly reserveAll: boolean;
  readonly reserveKw: string;
}

const kw = (watts: number): string => String(Math.round(watts / 10) / 100).replace(".", ",");

export function draftOf(policy: StorageTenantPolicy): DraftBand[] {
  return policy.bands.map((band) => ({
    from: String(band.fromSocPercent),
    dischargeKw: kw(band.dischargeW),
    reserveAll: band.chargeReserveW === null,
    reserveKw: band.chargeReserveW === null ? "" : kw(band.chargeReserveW),
  }));
}

/** Eine Zahl aus einem Feld — Komma oder Punkt; leer oder Unsinn ist `null`. */
function parse(text: string): number | null {
  const value = Number(text.trim().replace(",", "."));
  return text.trim() === "" || !Number.isFinite(value) ? null : value;
}

export type DraftErrors = Record<number, string>;

/** Aus dem Formular die Regel — oder je Zeile, was nicht stimmt. */
export function policyOf(draft: readonly DraftBand[]): { policy: StorageTenantPolicy | null; errors: DraftErrors } {
  const errors: DraftErrors = {};
  const bands: Band[] = [];
  draft.forEach((row, index) => {
    const from = parse(row.from);
    const discharge = parse(row.dischargeKw);
    const reserve = row.reserveAll ? null : parse(row.reserveKw);
    if (from === null || from < 0 || from > 100) errors[index] = "Speicherstand zwischen 0 und 100 %";
    else if (index === 0 && from !== 0) errors[index] = "Die erste Stufe beginnt bei 0 %";
    else if (index > 0 && bands[index - 1] !== undefined && from <= bands[index - 1]!.fromSocPercent)
      errors[index] = "Höher als die Stufe davor";
    else if (discharge === null || discharge < 0 || discharge > MAX_KW) errors[index] = "Abgabe zwischen 0 und 100 kW";
    else if (!row.reserveAll && (reserve === null || reserve < 0 || reserve > MAX_KW))
      errors[index] = "Ladereserve zwischen 0 und 100 kW — oder „alles“";
    bands.push({
      fromSocPercent: from ?? 0,
      dischargeW: Math.round((discharge ?? 0) * 1000),
      chargeReserveW: reserve === null ? null : Math.round(reserve * 1000),
    });
  });
  if (draft.length === 0 || draft.length > MAX_BANDS) errors[-1] = `1 bis ${MAX_BANDS} Stufen`;
  return { policy: Object.keys(errors).length === 0 ? { bands } : null, errors };
}

/** Eine Stufe in Worten — für die Ansicht ohne Formular. */
export function bandText(band: Band): { readonly discharge: string; readonly reserve: string } {
  return {
    discharge: band.dischargeW === 0 ? "gibt nichts ab" : `gibt bis ${kw(band.dischargeW)} kW ab`,
    reserve:
      band.chargeReserveW === null
        ? "behält die ganze Ladung"
        : band.chargeReserveW === 0
          ? "behält keine Ladung zurück"
          : `behält ${kw(band.chargeReserveW)} kW Ladung zurück`,
  };
}
